import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import {
    trackSbViewMoreCtaClick,
    trackRecentItems,
} from 'dibs-recent-history/exports/recentHistoryTracking';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { trackingConstants, eventNameConstants } from 'dibs-tracking';

import {
    trackRoomModuleClick,
    trackModuleClick,
} from '../../utils/tracking/searchBrowse/nullResultsTracking';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';

// components
import { SbRespNullSearchResultsTilesLoader } from './SbRespNullSearchResultsTilesLoader';
import { CategoryRooms } from 'dibs-buyer-layout/exports/CategoryLinks';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy';
import { SharedFollowSearchProvider } from '../../shared/SharedFollowSearchProvider/SharedFollowSearchProvider';
import { SbSharedNullSearchRelatedItems } from '../SbSharedNullSearchRelatedItems/SbSharedNullSearchRelatedItems';
import { SharedRecentlyFavoritedItems } from '../../shared/SharedRecentlyFavoritedItems/SharedRecentlyFavoritedItems';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import SharedYMAL from '../../shared/SharedYMAL/SharedYMAL';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton';
import { SharedRecentSales } from '../../shared/SharedRecentSales/SharedRecentSales';
import { SbSharedRecommendedItems } from '../SbSharedRecommendedItems/SbSharedRecommendedItems';
import { SbSharedControlledHeadingWrapper } from '../SbSharedControlledHeadingWrapper/SbSharedControlledHeadingWrapper';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';

// constants
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

const onRoomModuleClick = (e, { room }) => trackRoomModuleClick(room.type);

const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

const handleCarouselItemClick = ({ item, index, actionLabel }) => {
    trackModuleClick(actionLabel, index);
    trackRecentItems({
        type: ECOM_PRODUCT_CLICK,
        items: [item],
        startIndex: index + 1, // Tracking needs non zero index position.
        variant: 'null results page',
        list: `null results ${actionLabel} carousel`,
        eventName: EVENT_SELECT_ITEM,
    });
};

const handleCarouselItemImpression = ({ items, index, actionLabel }) => {
    trackRecentItems({
        type: ECOM_PRODUCT_IMPRESSION,
        items: items,
        startIndex: index + 1, // Tracking needs non zero index position.
        variant: 'null results page',
        list: `null results ${actionLabel} carousel`,
        eventName: EVENT_VIEW_ITEM_LIST,
    });
};

export const SbRespNullSearchResultsComponent = props => {
    const { user, relay, viewer, searchTerm, itemSearch, uriRef, isClient } = props;
    const { environment } = relay;
    const showNullPageModules = itemSearch?.soldItems?.totalResults;
    const { currency } = useCurrency();

    return (
        <SharedFollowSearchProvider
            user={user}
            uriRef={uriRef}
            searchTerm={searchTerm}
            itemSearch={itemSearch}
            thinTrackingEnabled={false}
        >
            {({
                showFollowButton,
                isFollowing,
                followSearchValue,
                refetchFollow,
                isCategorySearch,
                followPageServiceId,
            }) => (
                <Fragment>
                    <SbSharedPageBreadcrumbs itemSearch={itemSearch} />
                    <SbSharedControlledHeadingWrapper itemSearch={itemSearch}>
                        {Heading => (
                            <>
                                <SbSharedTitle
                                    itemSearch={itemSearch}
                                    searchTerm={searchTerm}
                                    Heading={Heading}
                                    showFollowButton={showFollowButton}
                                >
                                    <SharedFollowTopButton
                                        user={user}
                                        showFollowButton={showFollowButton}
                                        followSearchValue={followSearchValue}
                                        isFollowing={isFollowing}
                                        refetchFollow={refetchFollow}
                                        isCategorySearch={isCategorySearch}
                                        itemSearch={itemSearch}
                                        serviceId={followPageServiceId}
                                        className={dibsCss.plSmall}
                                    />
                                </SbSharedTitle>
                                {showNullPageModules ? (
                                    <div
                                        className={classnames(
                                            dibsCss.flex,
                                            dibsCss.flexCol,
                                            dibsCss.wFull,
                                            dibsCss.minW0,
                                            dibsCss.pySmall,
                                            dibsCss.px0
                                        )}
                                    >
                                        <SharedRecentSales
                                            viewer={viewer}
                                            user={user}
                                            itemSearch={itemSearch}
                                            isMobile={false}
                                            isClient={isClient}
                                            relay={relay}
                                        />
                                        <SharedYMAL
                                            user={user}
                                            itemSearch={itemSearch}
                                            isMobile={false}
                                            isClient={isClient}
                                            relay={relay}
                                        />
                                        <SbSharedRecommendedItems viewer={viewer} />
                                    </div>
                                ) : (
                                    <>
                                        <SbSharedNullSearchRelatedItems
                                            user={user}
                                            relay={relay}
                                            viewer={viewer}
                                            isMobile={false}
                                            shouldFetchFolder
                                            isClient={isClient}
                                            lazyLoadStartIndex={4}
                                            itemSearch={itemSearch}
                                            searchTerm={searchTerm}
                                            verticalOffset={VERTICAL_OFFSET_SB_RESP_IMG}
                                        />
                                        {isClient && (
                                            <SbRespFollowSearchBannerLazy
                                                user={user}
                                                buttonSize="large"
                                                uriRef={uriRef}
                                                isFollowing={isFollowing}
                                                refetchFollow={refetchFollow}
                                                showFollowButton={showFollowButton}
                                                followSearchValue={followSearchValue}
                                                isCategorySearch={isCategorySearch}
                                                itemSearch={itemSearch}
                                            />
                                        )}
                                        <SbRespNullSearchResultsTilesLoader
                                            environment={environment}
                                        />
                                    </>
                                )}
                                <RecentlyViewedItemsLazy
                                    centerTitle
                                    wrapperClassName={dibsCss.mbXxlarge}
                                    authModalLoader={authModalLoader}
                                    trackItemClick={handleCarouselItemClick}
                                    trackItemImpression={handleCarouselItemImpression}
                                    currency={currency}
                                />
                                {!showNullPageModules && (
                                    <>
                                        <SharedRecentlyFavoritedItems
                                            isClient={isClient}
                                            viewer={viewer}
                                            onViewMoreCtaClick={trackSbViewMoreCtaClick}
                                            onItemClick={handleCarouselItemClick}
                                            onItemImpression={handleCarouselItemImpression}
                                        />
                                        <CategoryRooms onClick={onRoomModuleClick} />
                                    </>
                                )}
                            </>
                        )}
                    </SbSharedControlledHeadingWrapper>
                </Fragment>
            )}
        </SharedFollowSearchProvider>
    );
};

SbRespNullSearchResultsComponent.propTypes = {
    user: PropTypes.object,
    uriRef: PropTypes.string,
    viewer: PropTypes.object,
    isClient: PropTypes.bool,
    searchTerm: PropTypes.string,
    relay: PropTypes.object.isRequired,
    itemSearch: PropTypes.object.isRequired,
};

export const SbRespNullSearchResults = createFragmentContainer(SbRespNullSearchResultsComponent, {
    user: graphql`
        fragment SbRespNullSearchResults_user on User {
            ...SbRespFollowSearchBanner_user
            ...SharedFollowSearchProvider_user
            ...SbSharedNullSearchRelatedItems_user
            ...SharedYMAL_user
            ...SharedFollowTopButton_user
            ...SharedRecentSales_user
        }
    `,
    viewer: graphql`
        fragment SbRespNullSearchResults_viewer on Viewer {
            ...SbSharedNullSearchRelatedItems_viewer
            ...SharedRecentlyFavoritedItems_viewer
            ...SbSharedRecommendedItems_viewer
            ...SharedRecentSales_viewer
        }
    `,
    itemSearch: graphql`
        fragment SbRespNullSearchResults_itemSearch on ItemSearchQueryConnection {
            ...SbSharedPageBreadcrumbs_itemSearch
            ...SharedFollowSearchProvider_itemSearch
            ...SbSharedNullSearchRelatedItems_itemSearch
            ...SbRespFollowSearchBanner_itemSearch
            ...SharedFollowSearchBanner_itemSearch
            ...SharedYMAL_itemSearch
                @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
            ...SharedFollowTopButton_itemSearch
            soldItems: soldResults(first: 28) {
                totalResults
            }
            ...SharedRecentSales_itemSearch
            ...SbSharedTitle_itemSearch
            ...SbSharedControlledHeadingWrapper_itemSearch
        }
    `,
});
